import React, { useEffect, useState } from 'react';
import {
  motion,
  MotionConfig,
  useReducedMotion,
} from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowRight,
  FiArrowUpRight,
  FiAward,
  FiCalendar,
  FiCheck,
  FiClock,
  FiCode,
  FiCompass,
  FiCpu,
  FiLayers,
  FiLock,
  FiMessageCircle,
  FiMic,
  FiMonitor,
  FiPlay,
  FiTarget,
  FiUsers,
  FiWifi,
  FiZap,
} from 'react-icons/fi';
import './EducationPage.css';

const outcomes = [
  {
    number: '01',
    eyebrow: 'DEFINE',
    icon: FiCompass,
    title: '문제를 자기 언어로 정의합니다.',
    copy: '정답을 기다리기보다, 누구의 어떤 불편을 바꾸고 싶은지 스스로 설명합니다.',
  },
  {
    number: '02',
    eyebrow: 'BUILD',
    icon: FiCode,
    title: 'AI와 작동하는 결과물을 만듭니다.',
    copy: '아이디어를 화면과 기능으로 설계하고, 오류를 고치며 끝까지 완성합니다.',
  },
  {
    number: '03',
    eyebrow: 'SHARE',
    icon: FiArrowUpRight,
    title: '발표하고, 피드백으로 개선합니다.',
    copy: '실제 링크로 공개해 반응을 확인하고 더 나은 선택을 다시 시도합니다.',
  },
];

const principles = [
  {
    number: '01',
    eyebrow: 'AI-PBL',
    icon: FiTarget,
    title: '설계 중심 프로젝트 학습',
    copy: '학생이 고른 문제를 질문으로 바꾸고, 기획부터 구현·발표까지 한 사이클을 완주합니다.',
    detail: ['문제 발견', '서비스 설계', 'AI 프로그래밍', '공개와 개선'],
    tone: 'blue',
  },
  {
    number: '02',
    eyebrow: 'DUAL COACHING',
    icon: FiUsers,
    title: 'AI 튜터 × 휴먼 멘토',
    copy: 'AI는 기술적 막힘을 빠르게 돕고, 멘토는 질문의 깊이와 성장 방향을 함께 설계합니다.',
    detail: ['24시간 AI 기술 지원', '전문가 성장 코칭'],
    tone: 'mint',
  },
  {
    number: '03',
    eyebrow: 'SAFE SANDBOX',
    icon: FiLock,
    title: '교육에 맞춘 전용 환경',
    copy: '안전 필터와 교육용 AI 지시 제어를 적용한 나니아랩 환경에서 마음껏 실험합니다.',
    detail: ['통제된 실습 공간', '실행·배포까지 한곳에서'],
    tone: 'sand',
  },
];

const levels = [
  {
    number: '01',
    label: 'AI INTERNSHIP',
    duration: '1개월',
    title: 'AI와 친해지고,\n첫 프로그램을 완성합니다.',
    copy: '게임을 기획하고 나니아랩 소프트웨어를 익히며, 말로 요청한 아이디어가 실제 화면이 되는 경험을 시작합니다.',
    skills: ['AI와 대화하는 법', '기본 게임 기획', '첫 실행과 공유'],
    projects: ['지렁이·벽돌깨기 게임', '카드 뒤집기 게임'],
    outcome: '흥미와 기본기',
  },
  {
    number: '02',
    label: 'AI-PBL',
    duration: '12개월',
    title: '매달 새로운 문제를,\n나만의 서비스로 만듭니다.',
    copy: '서비스를 직접 기획하고 AI API를 활용한 소프트웨어를 반복 개발하며, 포트폴리오를 차곡차곡 쌓습니다.',
    skills: ['서비스 기획', 'AI 프로그래밍', '매월 프로젝트 완주'],
    projects: ['AI 숙제 도우미', '디지털 보드게임'],
    outcome: '개인 포트폴리오',
  },
  {
    number: '03',
    label: 'SERVICE LAUNCH',
    duration: '12개월',
    title: '고객의 요구를 읽고,\n팀으로 서비스를 출시합니다.',
    copy: '실제 요구를 바탕으로 역할을 나누고 협업하며, 연구개발형 프로젝트를 기획부터 공개까지 경험합니다.',
    skills: ['팀 단위 협업', '고객 요구 분석', '실전 서비스 공개'],
    projects: ['행정 지원 AI 에이전트', '제조 데이터 프로젝트'],
    outcome: '팀 프로젝트 론칭',
  },
  {
    number: '04',
    label: 'ENTREPRENEURSHIP',
    duration: '심화 단계',
    title: '아이디어를 가치로,\n창업의 전 과정을 경험합니다.',
    copy: '시장 문제를 분석하고 MVP를 만든 뒤, 모의 투자와 창업경진대회를 통해 아이디어를 설득하는 법까지 배웁니다.',
    skills: ['시장 문제 정의', 'MVP 제작', '모의 투자·IR'],
    projects: ['맞춤형 관광 서비스 MVP', '사회문제 해결형 서비스'],
    outcome: '창업 도전',
  },
];

const sprintWeeks = [
  {
    week: 'WEEK 01',
    title: '기획',
    copy: '이번 달의 문제를 고르고, 사용자와 핵심 기능을 구체화합니다.',
    icon: FiCompass,
  },
  {
    week: 'WEEK 02',
    title: '심화',
    copy: 'AI 튜터와 기능을 발전시키고, 멘토 피드백으로 방향을 점검합니다.',
    icon: FiZap,
  },
  {
    week: 'WEEK 03',
    title: '완성',
    copy: '기능을 끝까지 구현하고 디버깅하며, 발표할 이야기를 정리합니다.',
    icon: FiCode,
  },
  {
    week: 'WEEK 04',
    title: '공개',
    copy: 'Narnia Day에서 프로젝트를 발표하고, 받은 반응을 다음 개선으로 연결합니다.',
    icon: FiPlay,
  },
];

const levelPreviewMeta = {
  '01': {
    type: 'game',
    domain: 'play.narnialab.app',
    status: 'PLAYABLE BUILD',
  },
  '02': {
    type: 'portfolio',
    domain: 'portfolio.narnialab.app',
    status: 'PORTFOLIO READY',
  },
  '03': {
    type: 'launch',
    domain: 'student-service.narnialab.app',
    status: 'NOW ONLINE',
  },
  '04': {
    type: 'venture',
    domain: 'pitch.narnialab.app',
    status: 'PITCH READY',
  },
};

const LevelPreview = ({ level }) => {
  const preview = levelPreviewMeta[level.number];

  return (
    <div
      className={`education-level-preview education-level-preview--${preview.type}`}
      aria-hidden="true"
    >
      <header>
        <span><i /><i /><i /></span>
        <small>{preview.domain}</small>
      </header>

      {preview.type === 'game' && (
        <div className="education-level-preview__body education-level-preview__game">
          <div className="education-level-preview__game-status">
            <span>FIRST GAME</span>
            <b>SCORE 120</b>
          </div>
          <div className="education-level-preview__game-board">
            <span className="education-level-preview__snake">
              <i /><i /><i /><i /><i />
            </span>
            <span className="education-level-preview__bricks">
              <i /><i /><i /><i />
            </span>
            <b />
            <em />
          </div>
        </div>
      )}

      {preview.type === 'portfolio' && (
        <div className="education-level-preview__body education-level-preview__portfolio">
          <span>MY PROJECTS</span>
          <strong>PROJECT COLLECTION</strong>
          <div>
            <i><b /></i>
            <i><b /></i>
            <i><b /></i>
          </div>
        </div>
      )}

      {preview.type === 'launch' && (
        <div className="education-level-preview__body education-level-preview__launch">
          <span><i /> LIVE SERVICE</span>
          <strong>PUBLIC LINK</strong>
          <div>
            <span><small>USERS</small><b>+24</b></span>
            <span><small>FEEDBACK</small><b>4.8</b></span>
          </div>
          <i><b /><b /><b /><b /><b /></i>
        </div>
      )}

      {preview.type === 'venture' && (
        <div className="education-level-preview__body education-level-preview__venture">
          <span>FROM MVP TO MARKET</span>
          <strong>MY STARTUP</strong>
          <div>
            <span><b>IDEA</b><i /></span>
            <em />
            <span><b>MVP</b><i /></span>
            <em />
            <span><b>PITCH</b><i /></span>
          </div>
        </div>
      )}

      <footer>
        <span><i /> {preview.status}</span>
        <b>{level.number}</b>
      </footer>
    </div>
  );
};

const reveal = {
  hidden: { opacity: 0, y: 42 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.82,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const revealGroup = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.08,
      staggerChildren: 0.1,
    },
  },
};

const EducationPage = () => {
  const prefersReducedMotion = useReducedMotion();
  const [activeLevel, setActiveLevel] = useState(1);
  const selectedLevel = levels[activeLevel];

  useEffect(() => {
    document.documentElement.classList.add('education-page-active');
    window.scrollTo(0, 0);

    return () => {
      document.documentElement.classList.remove('education-page-active');
    };
  }, []);

  const handleLevelKeyDown = (event, currentIndex) => {
    const lastIndex = levels.length - 1;
    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight') nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    if (event.key === 'ArrowLeft') nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === currentIndex) return;

    event.preventDefault();
    setActiveLevel(nextIndex);

    const focusNextTab = () => {
      document.getElementById(`education-level-tab-${nextIndex}`)?.focus();
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(focusNextTab);
    } else {
      focusNextTab();
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="education-page">
        <Helmet>
          <title>나니아랩 교육소개 | AI-PBL 온라인 프로젝트 교육</title>
          <meta
            name="description"
            content="초등 4학년부터 고등학생까지, AI와 함께 문제를 설계하고 매달 작동하는 서비스를 완성하는 나니아랩 온라인 AI-PBL 교육을 소개합니다."
          />
          <link rel="canonical" href="https://narnia-lab.com/education" />
          <meta property="og:type" content="website" />
          <meta property="og:title" content="나니아랩 교육소개 | 생각을 서비스로 만드는 AI-PBL" />
          <meta
            property="og:description"
            content="코딩을 따라 쓰는 데서 멈추지 않고, 아이디어를 실제 서비스로 완성하는 프로젝트형 AI 교육."
          />
          <meta property="og:url" content="https://narnia-lab.com/education" />
          <meta property="og:image" content="https://narnia-lab.com/logo.jpg" />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content="https://narnia-lab.com/education" />
          <meta property="twitter:title" content="나니아랩 교육소개 | 생각을 서비스로 만드는 AI-PBL" />
          <meta
            property="twitter:description"
            content="코딩을 따라 쓰는 데서 멈추지 않고, 아이디어를 실제 서비스로 완성하는 프로젝트형 AI 교육."
          />
          <meta property="twitter:image" content="https://narnia-lab.com/logo.jpg" />
        </Helmet>

        <a className="education-skip-link" href="#education-main">
          본문으로 바로가기
        </a>

        <main id="education-main">
          <section className="education-hero" aria-labelledby="education-hero-title">
            <div className="education-hero__grid" aria-hidden="true" />
            <div className="education-hero__glow education-hero__glow--blue" aria-hidden="true" />
            <div className="education-hero__glow education-hero__glow--peach" aria-hidden="true" />

            <div className="education-shell education-hero__layout">
              <motion.div
                className="education-hero__copy"
                variants={revealGroup}
                initial={prefersReducedMotion ? false : 'hidden'}
                animate="visible"
              >
                <motion.span className="education-eyebrow" variants={reveal}>
                  <i aria-hidden="true" />
                  NARNIA LAB ONLINE AI EDUCATION
                </motion.span>
                <motion.h1 id="education-hero-title" variants={reveal}>
                  AI를 배우는 데서,
                  <br />
                  <span>AI로 세상을 만드는 데까지.</span>
                </motion.h1>
                <motion.p variants={reveal}>
                  코딩을 따라 쓰는 수업이 아닙니다. 아이가 발견한 문제를 AI와 설계하고,
                  작동하는 프로그램으로 완성해 실제 사용자에게 공개합니다.
                </motion.p>

                <motion.div className="education-hero__actions" variants={reveal}>
                  <a className="education-button education-button--ink" href="#education-roadmap">
                    커리큘럼 보기
                    <FiArrowRight aria-hidden="true" />
                  </a>
                  <a
                    className="education-link"
                    href="https://pf.kakao.com/_DZhmn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    교육 상담하기
                    <FiArrowUpRight aria-hidden="true" />
                  </a>
                </motion.div>

                <motion.div className="education-hero__proof" variants={reveal}>
                  <span>
                    <strong>1 MONTH</strong>
                    프로젝트 하나
                  </span>
                  <span>
                    <strong>1 : 4</strong>
                    최대 그룹 코칭
                  </span>
                  <span>
                    <strong>10 HOURS</strong>
                    월 밀착 코칭
                  </span>
                </motion.div>
              </motion.div>

              <motion.div
                className="education-hero__visual"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 38, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 1,
                  delay: 0.2,
                  ease: [0.22, 1, 0.36, 1],
                }}
                aria-label="아이디어가 질문, 설계, 제작, 공개를 거쳐 실제 서비스로 성장하는 과정"
                role="img"
              >
                <div className="education-orbit" aria-hidden="true">
                  <div className="education-orbit__halo education-orbit__halo--outer" />
                  <div className="education-orbit__halo education-orbit__halo--inner" />
                  <span className="education-orbit__node education-orbit__node--question">
                    <i>01</i>
                    질문
                  </span>
                  <span className="education-orbit__node education-orbit__node--design">
                    <i>02</i>
                    설계
                  </span>
                  <span className="education-orbit__node education-orbit__node--build">
                    <i>03</i>
                    제작
                  </span>
                  <span className="education-orbit__node education-orbit__node--launch">
                    <i>04</i>
                    공개
                  </span>
                  <div className="education-orbit__core">
                    <span>N</span>
                    <small>아이디어</small>
                    <strong>실제 서비스</strong>
                  </div>
                </div>

                <div className="education-project-window" aria-hidden="true">
                  <header>
                    <span><i /><i /><i /></span>
                    <small>festival-now.narnialab.app</small>
                    <b><i /> LIVE</b>
                  </header>
                  <div className="education-project-window__body">
                    <div>
                      <small>STUDENT PROJECT</small>
                      <strong>축제 대기시간을<br />한눈에 확인해요.</strong>
                    </div>
                    <ul>
                      <li><span>과학 체험</span><b>12분</b></li>
                      <li><span>미니 게임</span><b>바로 입장</b></li>
                      <li><span>AI 포토존</span><b>7분</b></li>
                    </ul>
                  </div>
                  <footer>
                    <span><i /><i /><i /></span>
                    <b>첫 사용자가 들어왔어요.</b>
                  </footer>
                </div>
              </motion.div>
            </div>

            <a className="education-hero__scroll" href="#education-outcomes">
              <span>SCROLL TO LEARN</span>
              <i aria-hidden="true" />
            </a>
          </section>

          <section
            className="education-outcomes"
            id="education-outcomes"
            aria-labelledby="education-outcomes-title"
          >
            <div className="education-shell">
              <motion.div
                className="education-section-heading education-section-heading--split"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.45 }}
              >
                <div>
                  <span className="education-kicker">WHAT CHANGES</span>
                  <h2 id="education-outcomes-title">
                    결과물과 함께,
                    <br />
                    <span>생각하는 힘이 자랍니다.</span>
                  </h2>
                </div>
                <p>
                  도구를 배우는 데서 멈추지 않습니다. 문제를 발견하고, 선택을 설명하고,
                  피드백으로 다시 고치는 경험을 아이의 것으로 만듭니다.
                </p>
              </motion.div>

              <motion.div
                className="education-outcome-grid"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.22 }}
                role="list"
              >
                {outcomes.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.article
                      className="education-outcome-card"
                      key={item.number}
                      variants={reveal}
                      role="listitem"
                    >
                      <header>
                        <span>{item.number}</span>
                        <small>{item.eyebrow}</small>
                        <Icon aria-hidden="true" />
                      </header>
                      <h3>{item.title}</h3>
                      <p>{item.copy}</p>
                      <i className="education-outcome-card__line" aria-hidden="true" />
                    </motion.article>
                  );
                })}
              </motion.div>
            </div>
          </section>

          <section
            className="education-architect"
            id="education-architect"
            aria-labelledby="education-architect-title"
          >
            <div className="education-shell education-architect__layout">
              <motion.div
                className="education-architect__copy"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <span className="education-kicker">CODER TO ARCHITECT</span>
                <h2 id="education-architect-title">
                  따라 만드는 코더에서,
                  <br />
                  <span>문제를 설계하는 아키텍트로.</span>
                </h2>
                <p>
                  AI 시대의 핵심은 문법을 얼마나 외웠는지가 아니라, 어떤 문제를 발견하고
                  AI에게 무엇을 요청해 끝까지 해결하는가에 있습니다.
                </p>
                <blockquote>
                  “기술보다 생각을,
                  <br />
                  도구보다 사람을 세웁니다.”
                </blockquote>
              </motion.div>

              <motion.div
                className="education-architect__compare"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.35 }}
              >
                <article className="education-compare-card education-compare-card--coder">
                  <header><span>BEFORE</span><b>CODER</b></header>
                  <strong>주어진 설명서와<br />정답을 따라갑니다.</strong>
                  <ul>
                    <li><FiCheck aria-hidden="true" /> 기능을 그대로 모방하기</li>
                    <li><FiCheck aria-hidden="true" /> 정해진 오류만 수정하기</li>
                    <li><FiCheck aria-hidden="true" /> 결과를 제출하고 끝내기</li>
                  </ul>
                </article>
                <div className="education-compare-shift" aria-hidden="true">
                  <span>AI-PBL</span>
                  <FiArrowRight />
                </div>
                <article className="education-compare-card education-compare-card--architect">
                  <header><span>AFTER</span><b>ARCHITECT</b></header>
                  <strong>세상의 문제를<br />발견하고 설계합니다.</strong>
                  <ul>
                    <li><FiCheck aria-hidden="true" /> 필요한 질문부터 만들기</li>
                    <li><FiCheck aria-hidden="true" /> AI와 해결 방법 설계하기</li>
                    <li><FiCheck aria-hidden="true" /> 공개하고 다시 개선하기</li>
                  </ul>
                </article>
              </motion.div>
            </div>
          </section>

          <section
            className="education-system"
            id="education-system"
            aria-labelledby="education-system-title"
          >
            <div className="education-shell">
              <motion.div
                className="education-section-heading"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.42 }}
              >
                <span className="education-kicker">HOW NARNIA WORKS</span>
                <h2 id="education-system-title">
                  혼자 막히지 않고,
                  <br />
                  <span>대신 만들어주지도 않습니다.</span>
                </h2>
                <p>
                  설계 중심 프로젝트, AI와 사람의 이중 코칭, 안전한 전용 환경이
                  아이의 주도성을 지키면서 완주를 돕습니다.
                </p>
              </motion.div>

              <motion.div
                className="education-principle-grid"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.18 }}
              >
                {principles.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.article
                      className={`education-principle-card education-principle-card--${item.tone}`}
                      key={item.number}
                      variants={reveal}
                    >
                      <header>
                        <span>{item.number}</span>
                        <small>{item.eyebrow}</small>
                        <Icon aria-hidden="true" />
                      </header>
                      {item.number === '01' && (
                        <div className="education-pbl-visual" aria-hidden="true">
                          <div className="education-pbl-visual__question">
                            <span>?</span>
                            <small>MY QUESTION</small>
                            <strong>무엇을<br />바꾸고 싶을까?</strong>
                          </div>
                          <div className="education-pbl-visual__route">
                            <i><b /></i>
                            <span>THINK</span>
                            <i><b /></i>
                            <span>DESIGN</span>
                            <i><b /></i>
                          </div>
                          <div className="education-pbl-visual__result">
                            <header>
                              <span><i /><i /><i /></span>
                              <small><i /> LIVE</small>
                            </header>
                            <div>
                              <span />
                              <span />
                              <strong>MY SERVICE</strong>
                            </div>
                          </div>
                        </div>
                      )}
                      <h3>{item.title}</h3>
                      <p>{item.copy}</p>
                      <ul>
                        {item.detail.map((detail) => (
                          <li key={detail}><FiCheck aria-hidden="true" /> {detail}</li>
                        ))}
                      </ul>
                      {item.number === '01' && (
                        <div className="education-pbl-flow" aria-label="AI-PBL 학습 흐름">
                          <span>QUESTION</span><i />
                          <span>DESIGN</span><i />
                          <span>BUILD</span><i />
                          <span>LIVE</span>
                        </div>
                      )}
                    </motion.article>
                  );
                })}
              </motion.div>
            </div>
          </section>

          <section
            className="education-roadmap"
            id="education-roadmap"
            aria-labelledby="education-roadmap-title"
          >
            <div className="education-roadmap__kinetic" aria-hidden="true">
              <span>LEARN · BUILD · LAUNCH · LEAD</span>
              <span>LEARN · BUILD · LAUNCH · LEAD</span>
            </div>

            <div className="education-shell education-roadmap__content">
              <motion.div
                className="education-section-heading education-section-heading--light"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              >
                <span className="education-kicker">GROWTH ROADMAP</span>
                <h2 id="education-roadmap-title">
                  첫 게임에서,
                  <br />
                  <span>실제 서비스와 창업까지.</span>
                </h2>
                <p>
                  아이의 현재 경험과 목표에 맞춰 시작하고, 프로젝트의 깊이를 단계적으로
                  확장합니다.
                </p>
              </motion.div>

              <div className="education-levels">
                <div className="education-level-tabs" role="tablist" aria-label="교육 성장 단계">
                  {levels.map((level, index) => (
                    <button
                      key={level.number}
                      id={`education-level-tab-${index}`}
                      type="button"
                      role="tab"
                      aria-selected={activeLevel === index}
                      aria-controls="education-level-panel"
                      tabIndex={activeLevel === index ? 0 : -1}
                      onClick={() => setActiveLevel(index)}
                      onKeyDown={(event) => handleLevelKeyDown(event, index)}
                    >
                      <i>{level.number}</i>
                      <span>{level.label}</span>
                      <small>{level.duration}</small>
                    </button>
                  ))}
                </div>

                <motion.article
                  key={selectedLevel.number}
                  id="education-level-panel"
                  className="education-level-panel"
                  role="tabpanel"
                  aria-labelledby={`education-level-tab-${activeLevel}`}
                  initial={prefersReducedMotion ? false : { opacity: 0, x: 28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="education-level-panel__main">
                    <header>
                      <span>LEVEL {selectedLevel.number}</span>
                      <small>{selectedLevel.duration}</small>
                    </header>
                    <h3>
                      {selectedLevel.title.split('\n').map((line) => (
                        <React.Fragment key={line}>
                          {line}
                          <br />
                        </React.Fragment>
                      ))}
                    </h3>
                    <p>{selectedLevel.copy}</p>
                    <ul>
                      {selectedLevel.skills.map((skill) => (
                        <li key={skill}><FiCheck aria-hidden="true" /> {skill}</li>
                      ))}
                    </ul>
                  </div>
                  <aside>
                    <span>EXPECTED OUTCOME</span>
                    <LevelPreview level={selectedLevel} />
                    <strong>{selectedLevel.outcome}</strong>
                    <small>대표 프로젝트 예시</small>
                    {selectedLevel.projects.map((project) => (
                      <p key={project}><i aria-hidden="true" /> {project}</p>
                    ))}
                  </aside>
                </motion.article>
              </div>
            </div>
          </section>

          <section
            className="education-sprint"
            id="education-sprint"
            aria-labelledby="education-sprint-title"
          >
            <div className="education-shell">
              <motion.div
                className="education-section-heading education-section-heading--light education-section-heading--split"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.42 }}
              >
                <div>
                  <span className="education-kicker">4-WEEK PBL SPRINT</span>
                  <h2 id="education-sprint-title">
                    한 달에 하나.
                    <br />
                    <span>기획하고, 만들고, 보여줍니다.</span>
                  </h2>
                </div>
                <p>
                  매달 새로운 주제를 고르고, 4주의 리듬 안에서 생각을 실제 결과물로
                  바꿉니다. 완성 경험이 다음 도전을 움직입니다.
                </p>
              </motion.div>

              <motion.div
                className="education-sprint-timeline"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.22 }}
                role="list"
              >
                {sprintWeeks.map((week, index) => {
                  const Icon = week.icon;
                  return (
                    <motion.article key={week.week} variants={reveal} role="listitem">
                      <header>
                        <span>{week.week}</span>
                        <Icon aria-hidden="true" />
                      </header>
                      <i className="education-sprint-timeline__progress" aria-hidden="true">
                        <b>{String(index + 1).padStart(2, '0')}</b>
                      </i>
                      <h3>{week.title}</h3>
                      <p>{week.copy}</p>
                    </motion.article>
                  );
                })}
              </motion.div>

              <motion.div
                className="education-coaching"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.22 }}
              >
                <motion.article className="education-coaching__ai" variants={reveal}>
                  <header>
                    <span><FiCpu aria-hidden="true" /></span>
                    <small>AI TUTOR</small>
                  </header>
                  <h3>기술적인 막힘은<br />AI가 바로 이어줍니다.</h3>
                  <p>
                    프롬프트와 개발 내용을 바탕으로 기술 질문을 24시간 지원해,
                    아이가 시도를 멈추지 않도록 돕습니다.
                  </p>
                  <div aria-hidden="true">
                    <span><i>N</i> 어디에서 막혔나요?</span>
                    <span>로그인 뒤 첫 화면이 안 보여요.</span>
                    <span><i>N</i> 화면과 데이터 연결부터 같이 확인해 볼게요.</span>
                  </div>
                </motion.article>

                <motion.article className="education-coaching__mentor" variants={reveal}>
                  <header>
                    <span><FiUsers aria-hidden="true" /></span>
                    <small>HUMAN MENTOR</small>
                  </header>
                  <h3>아이의 다음 성장은<br />사람이 함께 설계합니다.</h3>
                  <p>
                    UNIST 석·박사 및 창업·개발 분야 전문가 멘토진이 질문의 깊이,
                    프로젝트 방향, 발표와 협업을 코칭합니다.
                  </p>
                  <ul>
                    <li><FiMessageCircle aria-hidden="true" /> 질문을 더 선명하게</li>
                    <li><FiLayers aria-hidden="true" /> 프로젝트를 더 탄탄하게</li>
                    <li><FiAward aria-hidden="true" /> 성장을 다음 기회로</li>
                  </ul>
                </motion.article>
              </motion.div>
            </div>
          </section>

          <section className="education-operation" aria-labelledby="education-operation-title">
            <div className="education-shell">
              <motion.div
                className="education-section-heading education-section-heading--split"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.42 }}
              >
                <div>
                  <span className="education-kicker">LEARNING SYSTEM</span>
                  <h2 id="education-operation-title">
                    학원 시간표가 아니라,
                    <br />
                    <span>아이의 몰입에 맞춥니다.</span>
                  </h2>
                </div>
                <p>
                  바쁜 학업 일정 속에서도 프로젝트를 포기하지 않도록, 월 단위 토큰과
                  자유 예약 방식으로 학습 리듬을 설계합니다.
                </p>
              </motion.div>

              <motion.div
                className="education-operation-grid"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.22 }}
              >
                <motion.article variants={reveal}>
                  <span><FiClock aria-hidden="true" /></span>
                  <small>MONTHLY COACHING</small>
                  <strong>10</strong>
                  <h3>월 10시간 밀착 코칭</h3>
                  <p>1토큰을 1시간 수업으로 사용하며, 프로젝트의 흐름에 맞춰 예약합니다.</p>
                </motion.article>
                <motion.article variants={reveal}>
                  <span><FiUsers aria-hidden="true" /></span>
                  <small>SMALL GROUP</small>
                  <strong>1:4</strong>
                  <h3>최대 1:4 그룹 멘토링</h3>
                  <p>각자의 프로젝트를 진행하면서도 또래의 질문과 시도에서 함께 배웁니다.</p>
                </motion.article>
                <motion.article variants={reveal}>
                  <span><FiCalendar aria-hidden="true" /></span>
                  <small>FLEXIBLE BOOKING</small>
                  <strong>100%</strong>
                  <h3>스케줄에 맞춘 자유 예약</h3>
                  <p>가장 집중할 수 있는 시간을 직접 고릅니다. 깊은 몰입을 위해 2시간 연속 수강을 권장합니다.</p>
                </motion.article>
              </motion.div>

              <p className="education-operation__note">
                세부 운영 시간과 그룹 구성은 과정 및 기수에 따라 달라질 수 있습니다.
              </p>
            </div>
          </section>

          <section
            className="education-stage"
            id="education-stage"
            aria-labelledby="education-stage-title"
          >
            <div className="education-shell">
              <motion.div
                className="education-section-heading"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.42 }}
              >
                <span className="education-kicker">BEYOND THE CLASS</span>
                <h2 id="education-stage-title">
                  만든 것을 보여줄 때,
                  <br />
                  <span>배움은 더 큰 무대로 이어집니다.</span>
                </h2>
                <p>
                  결과물을 혼자 보관하지 않습니다. 발표하고, 질문을 받고, 다른 창작자와
                  연결되며 다음 프로젝트의 기준을 높입니다.
                </p>
              </motion.div>

              <motion.div
                className="education-stage-grid"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <motion.article className="education-stage-card education-stage-card--day" variants={reveal}>
                  <div className="education-stage-card__visual" aria-hidden="true">
                    <span>NARNIA</span>
                    <strong>DAY</strong>
                    <i>MONTHLY</i>
                  </div>
                  <div>
                    <small>매월 마지막 주 토요일</small>
                    <h3>Narnia Day</h3>
                    <p>
                      프로젝트를 최종 점검하고 또래와 교류한 뒤, 직접 발표하고 피드백을
                      받는 월간 데모데이입니다.
                    </p>
                    <ul>
                      <li><FiCheck aria-hidden="true" /> 프로젝트 최종 점검</li>
                      <li><FiCheck aria-hidden="true" /> 발표와 사용자 피드백</li>
                    </ul>
                  </div>
                </motion.article>

                <motion.article className="education-stage-card education-stage-card--conference" variants={reveal}>
                  <div className="education-stage-card__visual" aria-hidden="true">
                    <span>NARNIA AI</span>
                    <strong>CONFERENCE</strong>
                    <i>ANNUAL</i>
                  </div>
                  <div>
                    <small>연 1회</small>
                    <h3>Narnia AI Conference</h3>
                    <p>
                      서로의 프로젝트와 아이디어를 나누고, 현업 IT·AI 연사의 이야기와
                      성장 기회를 만나는 청소년 AI 네트워킹 행사입니다.
                    </p>
                    <ul>
                      <li><FiCheck aria-hidden="true" /> 프로젝트·아이디어 교류</li>
                      <li><FiCheck aria-hidden="true" /> 현업 인사이트와 네트워킹</li>
                    </ul>
                  </div>
                </motion.article>
              </motion.div>
            </div>
          </section>

          <section
            className="education-start"
            id="education-start"
            aria-labelledby="education-start-title"
          >
            <div className="education-start__orb education-start__orb--one" aria-hidden="true" />
            <div className="education-start__orb education-start__orb--two" aria-hidden="true" />
            <div className="education-shell education-start__layout">
              <motion.div
                className="education-start__copy"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.38 }}
              >
                <span className="education-kicker">READY TO START</span>
                <h2 id="education-start-title">
                  무엇을 배우는지보다,
                  <br />
                  <span>무엇을 만들게 되는지 상담해 보세요.</span>
                </h2>
                <p>
                  아이의 관심사와 경험을 들려주시면, 지금 시작하기 좋은 단계와
                  프로젝트 방향을 함께 안내해 드립니다.
                </p>
                <div className="education-start__actions">
                  <a
                    className="education-button education-button--light"
                    href="https://narnia-lab.com/apply/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    온라인 교육 신청하기
                    <FiArrowUpRight aria-hidden="true" />
                  </a>
                  <a
                    className="education-button education-button--glass"
                    href="https://pf.kakao.com/_DZhmn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    교육 상담하기
                    <FiMessageCircle aria-hidden="true" />
                  </a>
                </div>
              </motion.div>

              <motion.aside
                className="education-start__info"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.38 }}
              >
                <header>
                  <span>ONLINE CLASS</span>
                  <i aria-hidden="true" />
                  <small>모집 안내</small>
                </header>
                <ul>
                  <li>
                    <span><FiUsers aria-hidden="true" /></span>
                    <div><small>대상</small><strong>초등 4학년 ~ 고등학생<br />AI 창업에 관심 있는 성인</strong></div>
                  </li>
                  <li>
                    <span><FiMonitor aria-hidden="true" /></span>
                    <div><small>준비 기기</small><strong>인터넷 연결 PC 또는 노트북</strong></div>
                  </li>
                  <li>
                    <span><FiWifi aria-hidden="true" /></span>
                    <div><small>수업 환경</small><strong>안정적인 인터넷 · 웹캠 · 마이크</strong></div>
                  </li>
                </ul>
                <footer>
                  <FiMic aria-hidden="true" />
                  <span>상담 후 아이에게 맞는 시작 단계를 안내합니다.</span>
                </footer>
              </motion.aside>
            </div>
          </section>
        </main>
      </div>
    </MotionConfig>
  );
};

export default EducationPage;
